import React, { useEffect } from 'react';
import useStyles from './styles';
import { baseInitialValues } from './constants';
import UserSearchInput from 'molecules/UserSearchInput';
import {
  Button,
  Avatar,
  CreditType,
  SearchUserResult,
  User,
  TextField,
} from '@elevator/ui';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { CreditFormValues, CreditInput } from 'types/credit';
import { useFormikContext } from 'formik';
import { useQuery } from '@apollo/client/react';
import { CREDIT_TYPES } from 'lib/graphql/queries/creditTypes';
import { IconButton, SelectChangeEvent } from '@mui/material';
import * as IconMDI from 'mdi-material-ui';

const Fields: React.FC<FieldsProps> = ({
  currentUser,
  onCancel,
  saveCredit,
  editMode = false,
}) => {
  const { classes } = useStyles();

  const {
    resetForm,
    submitForm,
    isValid,
    values,
    errors,
    touched,
    handleChange,
    setFieldValue,
    setFieldTouched,
    validateForm,
  } = useFormikContext<CreditFormValues>();

  useEffect(() => {
    validateForm();
  }, [values, validateForm]);

  // Keeps track of the search input text
  const [textEntry, setTextEntry] = React.useState('');
  const [isManuallyAddingCredit, setIsManuallyAddingCredit] = React.useState(editMode);
  const manualEntry = !values.autoLinked;

  const [stagedCredit, setStagedCredit] = React.useState<CreditInput | null>(null);
  const { data } = useQuery<any>(CREDIT_TYPES, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
  });

  const creditTypes: CreditType[] = data?.creditTypes || [];

  const onCreditTypeChange = (event: SelectChangeEvent<string>) => {
    setFieldValue('type', event.target.value);
    setFieldTouched('type', true);
    setStagedCredit({
      ...stagedCredit,
      type: event.target.value,
    });
  };

  /**
   * Handler function for when a user selects an option from an auto-suggested list of
   * users
   */
  const onUserSelected = (user: SearchUserResult) => {
    // Reset any active fields first

    let isCurrentUser = false;

    if (currentUser && currentUser.preferredUsername === user.preferredUsername)
      isCurrentUser = true;

    const newFormValues: CreditInput = {
      name: user.preferredUsername,
      autoLinked: true,
      email: user.email,
      isSubmitter: isCurrentUser,
      type: values.type,
      avatar: user.avatar,
    };
    // Update current state
    setStagedCredit(newFormValues);

    // Clear search input
    setTextEntry('');
  };

  /**
   * Handler function for when the user hits the 'save' button after editing a credit
   */
  const handleOnClickSave = async () => {
    if (isManuallyAddingCredit) {
      await submitForm();
    } else if (saveCredit && stagedCredit) {
      await saveCredit(stagedCredit);
    }
    onCancel();
  };

  const handleAddNew = () => {
    setIsManuallyAddingCredit(true);
    resetForm({
      values: {
        ...baseInitialValues,
        type: values.type,
      },
    });
  };

  /**
   * Handler function for when a user decides to clear (or 'unlink' rather) an auto-filled
   * form
   */
  const onClearUser = () => {
    resetForm({ values: baseInitialValues });
    setStagedCredit(null);
    setTextEntry('');
  };

  const renderLinkedBadge = () => {
    return (
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" alignItems="center">
          <Avatar
            username={stagedCredit.name}
            size="medium"
            src={stagedCredit.avatar?.sourceUrl}
            srcSet={stagedCredit.avatar?.sourceSet}
            alt={stagedCredit.name}
          />
          <Typography className={classes.userName} variant={'body2'}>
            <b>{stagedCredit.name}</b>
          </Typography>
        </Stack>
        {/**Unlink button*/}
        <IconButton className={classes.closeButton} onClick={onClearUser} size="large">
          <IconMDI.Close color={'primary'} />
        </IconButton>
      </Stack>
    );
  };

  const renderUserSelection = () => {
    if (stagedCredit && stagedCredit.name) {
      return renderLinkedBadge();
    }

    return (
      <UserSearchInput
        id={'search'}
        placeholder={'Username'}
        disableUnderline={true}
        onUserSelected={onUserSelected}
        textFieldInput={textEntry}
        setTextFieldInput={setTextEntry}
        disabled={false}
        isAddCreditOption={true}
        handleManualAddNew={handleAddNew}
      />
    );
  };

  const handleNameChange = (e: React.ChangeEvent) => {
    setFieldTouched('name');
    handleChange(e);
  };

  // Inline CreatorInfo fields for manual entry
  const renderCreatorInfo = () => {
    return (
      <>
        <Box marginBottom={1}>
          <TextField
            id={'name'}
            label={'Display Name'}
            required
            placeholder={'Young Jasper'}
            value={values.name}
            fullWidth
            disableUnderline
            onChange={handleNameChange}
            error={touched.name && Boolean(errors.name)}
            disabled={!manualEntry}
            helperText={
              touched.name && errors.name ? (
                <Typography variant="caption" color="error">
                  {errors.name}
                </Typography>
              ) : (
                ''
              )
            }
          />
        </Box>
        <TextField
          id={'email'}
          label={'Email'}
          placeholder={'artist@gmail.com'}
          value={values.email}
          fullWidth
          disableUnderline
          onChange={handleChange}
          error={Boolean(errors.email)}
          disabled={!manualEntry}
          hidden={values.autoLinked}
          helperText={errors.email || ''}
        />
      </>
    );
  };

  return (
    <Stack className={classes.addCreditFormRoot} direction="column" spacing={1}>
      <Stack direction="column" spacing={1}>
        <FormControl
          variant="filled"
          fullWidth
          required
          error={touched.type && Boolean(errors.type)}
        >
          <InputLabel id={'typeLabel'} shrink>
            Role
          </InputLabel>
          <Select
            id={'type'}
            labelId={'typeLabel'}
            label={'Role'}
            variant={'filled'}
            fullWidth
            disableUnderline
            value={values.type || ''}
            onChange={onCreditTypeChange}
            disabled={creditTypes.length === 0}
            displayEmpty
            renderValue={(selected) =>
              selected && typeof selected === 'string' ? (
                selected
              ) : (
                <Typography color="textSecondary">Select a role</Typography>
              )
            }
          >
            <MenuItem value="" disabled>
              <ListItemText primary="Select a role" />
            </MenuItem>
            {creditTypes.map((type) => (
              <MenuItem key={type.typeName} value={type.typeName}>
                <ListItemText primary={type.typeName} />
              </MenuItem>
            ))}
          </Select>
          {touched.type && errors.type && (
            <FormHelperText error>{errors.type}</FormHelperText>
          )}
        </FormControl>
        {!isManuallyAddingCredit ? renderUserSelection() : renderCreatorInfo()}
      </Stack>
      <Stack
        direction="row"
        justifyContent="flex-end"
        spacing={1}
        className={classes.dialogActionsContainer}
      >
        <Button
          size={'large'}
          variant={'text'}
          onClick={() =>
            editMode
              ? onCancel()
              : isManuallyAddingCredit
                ? setIsManuallyAddingCredit(false)
                : onCancel()
          }
        >
          Cancel
        </Button>
        <Button
          disabled={isManuallyAddingCredit ? !isValid : !stagedCredit}
          size={'large'}
          variant={'text'}
          onClick={handleOnClickSave}
        >
          Add
        </Button>
      </Stack>
    </Stack>
  );
};

export interface FieldsProps {
  onCancel: () => void;
  currentUser: User;
  saveCredit?: (credit: CreditInput) => Promise<void>;
  editMode?: boolean;
}

export default Fields;
